import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getObjetivoById } from "../../../api/broker/objetivo";
import {
  EditIndicadorFields,
  iDataIndicador,
  PostIndicadorFields,
} from "../../../interfaces/SuperAdmin/Pages/iDataIndicador";
import { iDataNivel } from "../../../interfaces/SuperAdmin/Pages/iDataNivel";
import { iDataObjetivo } from "../../../interfaces/SuperAdmin/Pages/iDataObjetivos";
import {
  deleteIndicadores,
  getIndicadoresByObjId,
  postIndicador,
  putIndicadorById,
} from "../../../api/broker/indicador";

interface SingleObjetivoContextProps {
  objetivo: iDataObjetivo | null;
  indicadores: iDataIndicador[];

  toEditIndicador: iDataIndicador | null;
  selectedIndicadores: string[];
  setSelectedIndicadores: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;

  isLoadingInfo: boolean;
  isEditing: boolean;
  isCreating: boolean;
  isDeleting: boolean;

  isEditModalOpen: boolean;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;

  handleEditIndicador: (indicador: iDataIndicador) => void;
  toggleEditModal: (isOpen: boolean) => void;
  toggleCreateModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;

  indicadorEdit: (
    data: EditIndicadorFields,
    id_indicador: string
  ) => Promise<void>;
  indicadorCreate: (data: PostIndicadorFields) => Promise<void>;
  indicadorDelete: (body: { ids: string[] }) => Promise<void>;
}

const SingleObjetivoContext = createContext<SingleObjetivoContextProps>(
  {} as SingleObjetivoContextProps
);

interface Props {
  children: React.ReactNode;
}

const SingleObjetivoProvider: FC<Props> = ({ children }) => {
  const { idOrganization, idObjetivo } = useParams();

  const [objetivo, setObjetivo] = useState<iDataObjetivo | null>(null);
  const [indicadores, setIndicadores] = useState<iDataIndicador[]>([]);
  const [toEditIndicador, setToEditIndicador] = useState<iDataIndicador | null>(
    null
  );
  const [selectedIndicadores, setSelectedIndicadores] = useState<string[]>([]);

  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchText, setSearchText] = useState("");

  // Función para extraer objetivo e indicadores
  const fetchInfo = async (id_objetivo: string) => {
    setIsLoadingInfo(true);
    try {
      const [objRes, indicRes] = await Promise.all([
        getObjetivoById(id_objetivo),
        getIndicadoresByObjId(id_objetivo),
      ]);

      console.log(objRes);
      if (objRes.data.statusCode === 200) {
        setObjetivo(objRes.data.body);
      } else {
        toast.error("Error al obtener el objetivo");
      }

      console.log(indicRes);
      if (indicRes.data.statusCode === 200) {
        setIndicadores(indicRes.data.body);
      } else {
        toast.error("Error al obtener los indicadores del objetivo");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener el objetivo y/o los indicadores");
    } finally {
      setIsLoadingInfo(false);
    }
  };

  // Crear indicador
  const indicadorCreate = async (indicador: PostIndicadorFields) => {
    setIsCreating(true);
    try {
      //Creamos el indicador
      const indicadorRes = await postIndicador(indicador);
      console.log(indicadorRes);

      if (indicadorRes.data.statusCode === 201) {
        setIndicadores((prev) => [...prev, indicadorRes.data.body]);
        toggleCreateModal(false);
        toast.success("Indicador creado exitosamente");
      } else {
        toast.error("Error al crear indicador");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear indicador");
    } finally {
      setIsCreating(false);
    }
  };

  // Edicion de nivel
  const indicadorEdit = async (
    data: EditIndicadorFields,
    id_indicador: string
  ) => {
    setIsEditing(true);
    try {
      const res = await putIndicadorById(data, id_indicador);
      console.log(res);
      if (res.data.statusCode === 200) {
        setIndicadores((prev) =>
          prev.map((indicador) => {
            if (indicador.id_indicador === id_indicador) {
              return res.data.body;
            }
            return indicador;
          })
        );
        toggleEditModal(false);
        toast.success("Nivel editado exitosamente");
      } else {
        toast.error("Error al editar indicador");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar indicador");
    } finally {
      setIsEditing(false);
    }
  };

  // Eliminar indicadores
  const indicadorDelete = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteIndicadores(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedIndicadores([]);
        setIndicadores((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_indicador))
        );
        toggleDeleteModal(false);
        toast.success("Indicadores eliminados exitosamente");
      } else {
        toast.error("Error al eliminar los indicadores");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los indicadores");
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir menu de editar indicador
  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setToEditIndicador(null);
    }
    setIsEditModalOpen(isOpen);
  };

  // Abrir menu de crear indicador
  const toggleCreateModal = (isOpen: boolean) => {
    setIsCreateModalOpen(isOpen);
  };

  // Abrir menu de eliminar indicadores
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Seleccionar indicador a editar
  const handleEditIndicador = (indicador: iDataIndicador) => {
    setToEditIndicador(indicador);
    toggleEditModal(true);
  };

  useEffect(() => {
    if (idObjetivo) {
      fetchInfo(idObjetivo);
    }
  }, [idObjetivo]);

  return (
    <SingleObjetivoContext.Provider
      value={{
        objetivo,
        indicadores,
        toEditIndicador,
        selectedIndicadores,
        searchText,
        isLoadingInfo,
        isEditing,
        isCreating,
        isDeleting,
        isEditModalOpen,
        isCreateModalOpen,
        isDeleteModalOpen,
        handleEditIndicador,
        toggleEditModal,
        toggleCreateModal,
        toggleDeleteModal,
        indicadorEdit,
        indicadorCreate,
        indicadorDelete,
        setSearchText,
        setSelectedIndicadores,
      }}
    >
      {children}
    </SingleObjetivoContext.Provider>
  );
};

const useSingleObjetivo = () => useContext(SingleObjetivoContext);

export { SingleObjetivoProvider, useSingleObjetivo };
